import type { ReactNode } from "react";
import { isPlatformFeatureEnabled } from "../../config/platformFeatures";

import {
  PanelDashboard,
  ActividadSection,
  EmbudoDashboard,
  PanelPipelineSection,
  PanelPronosticoSection,
} from "./sections/panel";
import {
  LeadsInbox,
  FuentesSection,
  CalificacionSection,
} from "./sections/leads";
import {
  ContactosLeadsDashboard,
  ContactosListSection,
  EmpresasSection,
} from "./sections/contactos";
import {
  PipelineSection,
  NegociosListSection,
  PronosticoSection,
  FacturasSection,
} from "./sections/negocios";
import {
  TareasDashboard,
  LlamadasPorLlamarSection,
  LlamadasHistorialSection,
  CampanasOutboundSection,
  ChatSection,
  ReunionesSection,
} from "./sections/tareas";
import {
  MarketingDashboard,
  LandingPagesSection,
} from "./sections/marketing";
import {
  AutomatizacionesDashboard,
  WorkflowsSection,
  EjecucionesSection,
} from "./sections/automatizaciones";
import ProspectingLive from "./sections/ProspectingLive";
import ChatRepliesSettings from "./sections/ChatRepliesSettings";
import IntegrationsSettings from "./sections/IntegrationsSettings";
import HubspotSettings from "./sections/HubspotSettings";
import EnlacesSection from "./sections/EnlacesSection";
import {
  TareasLive,
  NotasLive,
  TicketsLive,
  CorreosLive,
  ProductosLive,
  CotizacionesLive,
  CampanasLive,
  FormulariosLive,
  EnviosFormularioLive,
  SegmentosLive,
} from "./sections/crmObjectSections";
import ClaudeConnectionSettings from "./sections/ClaudeConnectionSettings";
import PipelinesSettings from "./sections/PipelinesSettings";
import AgentesLive from "./sections/AgentesLive";
import {
  AgendaDashboard,
  AgendaCalendar,
  BloqueosSection,
  DisponibilidadSection,
} from "./sections/agenda";

// ---------------------------------------------------------------------------
// Single source of truth for the CRM navigation tree. Drives:
//   - the apps-sidebar category/apps (appsConfig.tsx)
//   - the per-functionality sub-menu (CrmLayout)
//   - the nested routes (router/config.tsx via routes.tsx)
//
// Pattern mirrors Layouts/nuvex-access/config.tsx. Most sections are mock
// scaffolds (demo data, in-session CRUD); the "Leads → Bandeja de leads"
// section is LIVE and reads real leads from the API (landing form → CRM).
// ---------------------------------------------------------------------------

export interface NavItem {
  id: string; // route segment under the functionality
  label: string;
  icon: string; // remixicon class for the sub-menu
  element?: ReactNode; // the section to render (omitted for parents with children)
  children?: NavItem[]; // optional sub-menu (one nesting level); parent redirects to children[0]
  /**
   * Sólo se lista para administradores de plataforma. Es comodidad de la
   * navegación: la sección y su API se protegen por su cuenta.
   */
  platformOnly?: boolean;
}

export interface Functionality {
  slug: string; // route base: /crm/<slug>
  name: string;
  description: string;
  iconClass: string; // apps-sidebar icon
  color: string; // tailwind bg color for the app tile
  remixIcon: string; // sub-menu / header icon
  menu: NavItem[];
}

export const FUNCTIONALITIES: Functionality[] = [
  {
    slug: "panel",
    name: "Panel CRM",
    description: "Resumen de ventas, leads y actividad comercial",
    iconClass: "ri-pie-chart-line",
    color: "bg-indigo-500",
    remixIcon: "ri-dashboard-3-line",
    menu: [
      { id: "dashboard", label: "Dashboard", icon: "ri-dashboard-line", element: <PanelDashboard /> },
      { id: "actividad", label: "Actividad", icon: "ri-history-line", element: <ActividadSection /> },
      { id: "embudo", label: "Embudo de ventas", icon: "ri-filter-3-line", element: <EmbudoDashboard /> },
      { id: "pipeline", label: "Pipeline", icon: "ri-layout-column-line", element: <PanelPipelineSection /> },
      { id: "pronostico", label: "Pronóstico", icon: "ri-line-chart-line", element: <PanelPronosticoSection /> },
    ],
  },
  {
    slug: "contactos",
    name: "Contactos y Leads",
    description: "Captación de prospectos y directorio comercial",
    iconClass: "ri-contacts-book-line",
    color: "bg-cyan-500",
    remixIcon: "ri-contacts-book-line",
    menu: [
      { id: "dashboard", label: "Dashboard", icon: "ri-dashboard-line", element: <ContactosLeadsDashboard /> },
      { id: "bandeja", label: "Bandeja de leads", icon: "ri-inbox-line", element: <LeadsInbox /> },
      { id: "contactos", label: "Contactos", icon: "ri-user-line", element: <ContactosListSection /> },
      { id: "empresas", label: "Empresas", icon: "ri-building-line", element: <EmpresasSection /> },
    ],
  },
  {
    slug: "negocios",
    name: "Negocios",
    description: "Oportunidades, pipeline, cotizaciones y facturas",
    iconClass: "ri-shake-hands-line",
    color: "bg-emerald-500",
    remixIcon: "ri-shake-hands-line",
    menu: [
      { id: "pronostico", label: "Pronóstico", icon: "ri-line-chart-line", element: <PronosticoSection /> },
      { id: "pipeline", label: "Pipeline", icon: "ri-layout-column-line", element: <PipelineSection /> },
      // Cotizaciones y Facturas son documentos de un negocio: van dentro de «Negocios».
      // Sus rutas anteriores siguen abriendo (LEGACY_REDIRECTS en navPaths.ts).
      {
        id: "negocios",
        label: "Negocios",
        icon: "ri-briefcase-line",
        children: [
          { id: "lista", label: "Lista de negocios", icon: "ri-briefcase-line", element: <NegociosListSection /> },
          // Apagadas donde la plataforma no opera facturas/cotizaciones del CRM (WAYAK):
          // ni menú ni ruta (config/platformFeatures.ts).
          ...(isPlatformFeatureEnabled("crm-facturacion")
            ? [
                { id: "cotizaciones", label: "Cotizaciones", icon: "ri-file-list-2-line", element: <CotizacionesLive /> },
                { id: "facturas", label: "Facturas", icon: "ri-bill-line", element: <FacturasSection /> },
              ]
            : []),
        ],
      },
      { id: "productos", label: "Productos", icon: "ri-price-tag-3-line", element: <ProductosLive /> },
    ],
  },
  {
    slug: "tareas",
    name: "Tareas y actividades",
    description: "Pendientes, llamadas y reuniones",
    iconClass: "ri-task-line",
    color: "bg-amber-500",
    remixIcon: "ri-task-line",
    menu: [
      { id: "dashboard", label: "Dashboard", icon: "ri-dashboard-line", element: <TareasDashboard /> },
      { id: "tareas", label: "Tareas", icon: "ri-task-line", element: <TareasLive /> },
      { id: "notas", label: "Notas", icon: "ri-sticky-note-line", element: <NotasLive /> },
      { id: "tickets", label: "Tickets", icon: "ri-customer-service-2-line", element: <TicketsLive /> },
      { id: "correos", label: "Correos", icon: "ri-mail-line", element: <CorreosLive /> },
      { id: "chat", label: "Chat", icon: "ri-whatsapp-line", element: <ChatSection /> },
      {
        id: "llamadas",
        label: "Llamadas",
        icon: "ri-phone-line",
        children: [
          { id: "por-llamar", label: "Por llamar", icon: "ri-phone-find-line", element: <LlamadasPorLlamarSection /> },
          { id: "campanas", label: "Campañas", icon: "ri-rocket-2-line", element: <CampanasOutboundSection /> },
          { id: "historial", label: "Historial", icon: "ri-history-line", element: <LlamadasHistorialSection /> },
        ],
      },
      { id: "reuniones", label: "Reuniones", icon: "ri-calendar-event-line", element: <ReunionesSection /> },
    ],
  },
  {
    slug: "agenda",
    name: "Agenda",
    description: "Calendario de reservas, bloqueos y disponibilidad",
    iconClass: "ri-calendar-check-line",
    color: "bg-sky-500",
    remixIcon: "ri-calendar-2-line",
    menu: [
      { id: "dashboard", label: "Dashboard", icon: "ri-dashboard-line", element: <AgendaDashboard /> },
      { id: "calendario", label: "Calendario", icon: "ri-calendar-2-line", element: <AgendaCalendar /> },
      { id: "bloqueos", label: "Bloqueos", icon: "ri-lock-line", element: <BloqueosSection /> },
      { id: "disponibilidad", label: "Disponibilidad", icon: "ri-time-line", element: <DisponibilidadSection /> },
    ],
  },
  {
    slug: "marketing",
    name: "Marketing",
    description: "Campañas, formularios y landing pages",
    iconClass: "ri-megaphone-line",
    color: "bg-pink-500",
    remixIcon: "ri-megaphone-line",
    menu: [
      { id: "dashboard", label: "Dashboard", icon: "ri-dashboard-line", element: <MarketingDashboard /> },
      { id: "campanas", label: "Campañas", icon: "ri-rocket-line", element: <CampanasLive /> },
      { id: "formularios", label: "Formularios", icon: "ri-file-list-3-line", element: <FormulariosLive /> },
      { id: "envios", label: "Envíos de formularios", icon: "ri-inbox-archive-line", element: <EnviosFormularioLive /> },
      { id: "landing-pages", label: "Landing pages", icon: "ri-pages-line", element: <LandingPagesSection /> },
      // Enlaces cortos y QR del funnel (CC-CONSOLE #1528): <base de enlaces del tenant>/r/<código>
      { id: "enlaces", label: "Enlaces cortos y QR", icon: "ri-qr-code-line", element: <EnlacesSection /> },
    ],
  },
  {
    slug: "automatizaciones",
    name: "Automatizaciones",
    description: "Workflows, disparadores y ejecuciones",
    iconClass: "ri-robot-2-line",
    color: "bg-teal-500",
    remixIcon: "ri-flow-chart",
    menu: [
      { id: "dashboard", label: "Dashboard", icon: "ri-dashboard-line", element: <AutomatizacionesDashboard /> },
      { id: "prospeccion", label: "Prospección", icon: "ri-search-eye-line", element: <ProspectingLive /> },
      { id: "workflows", label: "Workflows", icon: "ri-git-branch-line", element: <WorkflowsSection /> },
      { id: "ejecuciones", label: "Ejecuciones", icon: "ri-play-circle-line", element: <EjecucionesSection /> },
    ],
  },
  {
    slug: "setting",
    name: "Configuración",
    description: "Fuentes de leads, calificación y segmentos",
    iconClass: "ri-settings-3-line",
    color: "bg-slate-500",
    remixIcon: "ri-settings-3-line",
    menu: [
      {
        id: "agentes",
        label: "Agentes",
        icon: "ri-customer-service-2-line",
        children: [
          { id: "humanos", label: "Humanos", icon: "ri-user-3-line", element: <AgentesLive filterType="human" /> },
          { id: "ia", label: "Agentes IA", icon: "ri-robot-2-line", element: <AgentesLive filterType="ai" /> },
        ],
      },
      {
        id: "pipelines",
        label: "Pipelines",
        icon: "ri-flow-chart",
        children: [
          { id: "categorias", label: "Categorías", icon: "ri-price-tag-3-line", element: <PipelinesSettings view="categorias" /> },
          { id: "tableros", label: "Pipelines", icon: "ri-flow-chart", element: <PipelinesSettings view="pipelines" /> },
          { id: "templates", label: "Plantillas", icon: "ri-layout-grid-line", element: <PipelinesSettings view="templates" /> },
        ],
      },
      { id: "integraciones", label: "Integraciones", icon: "ri-key-2-line", element: <IntegrationsSettings /> },
      { id: "hubspot", label: "HubSpot", icon: "ri-exchange-line", element: <HubspotSettings /> },
      { id: "claude", label: "Conexión con Claude", icon: "ri-robot-2-line", element: <ClaudeConnectionSettings /> },
      { id: "fuentes", label: "Fuentes", icon: "ri-global-line", element: <FuentesSection /> },
      { id: "calificacion", label: "Calificación", icon: "ri-medal-line", element: <CalificacionSection /> },
      { id: "segmentos", label: "Segmentos", icon: "ri-group-2-line", element: <SegmentosLive /> },
      { id: "chat", label: "Chat", icon: "ri-chat-quote-line", element: <ChatRepliesSettings /> },
      // «Demos» (CC-CONSOLE #1318) no se monta aquí: la opera la consola central
      // por su hub de plataformas; esta plataforma es la que recibe la cuenta.
    ],
  },
];

export const CRM_BASE = "/crm";

/** Path segment(s) for a menu item: "id" for a leaf, "id/firstChildId" for a parent. */
export const menuItemPath = (item: NavItem) =>
  item.children?.length ? `${item.id}/${item.children[0].id}` : item.id;

/** Default landing route for the whole section (first functionality dashboard). */
export const crmDefaultRoute = `${CRM_BASE}/${FUNCTIONALITIES[0].slug}/${menuItemPath(FUNCTIONALITIES[0].menu[0])}`;

/** Route to a functionality's first menu item (used by the apps-sidebar tiles). */
export const functionalityRoute = (f: Functionality) =>
  `${CRM_BASE}/${f.slug}/${menuItemPath(f.menu[0])}`;
